import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { isSameDay, isWithinInterval } from "date-fns";
import { fr } from "date-fns/locale";
import { Typography, Paper, Box } from "@mui/material";
import { EventAvailable } from "@mui/icons-material";

import "./AdminCalendar.css";
import { formatLocalDateKey, weekdayKeys } from "./calendarUtils";
import CalendarLegend from "components/bookingCalendar/CalendarLegend";

const AdminCalendar = ({
  unavailableDates = [],
  bookedDateRanges = [], // Updated to handle date ranges instead of single dates
  availableWeekdays = {},
  onDateChange,
  highlightedDate,
}) => {
  const handleDateSelection = (date) => {
    const key = formatLocalDateKey(date);
    onDateChange(date, key);
    return;
  };

  // Check if a date is within any booking range
  const isDateInBookingRange = (date) => {
    // Create a date object with time set to noon to avoid timezone issues
    const dateObj = new Date(date);
    dateObj.setHours(12, 0, 0, 0);

    return bookedDateRanges.some((range) => {
      if (!range.startDate || !range.endDate) return false;

      // Create new date objects for start and end with time set to noon
      const startDate = new Date(range.startDate);
      startDate.setHours(12, 0, 0, 0);

      const endDate = new Date(range.endDate);
      endDate.setHours(12, 0, 0, 0);

      // Check if dateObj is within the range (inclusive)
      return isWithinInterval(dateObj, {
        start: startDate,
        end: endDate,
      });
    });
  };

  const renderDayContents = (day, date) => {
    const dateKey = formatLocalDateKey(date);
    const weekday = weekdayKeys[date.getDay()];
    const isAllowedWeekday = availableWeekdays[weekday];
    const isSelected = highlightedDate && isSameDay(date, highlightedDate);
    const isBooked = isDateInBookingRange(date);
    const isBlocked = unavailableDates.includes(dateKey);
    const isNonAllowedWeekday = !isAllowedWeekday;

    let className = "custom-day ";

    // Admin view styling
    if (isBooked) {
      className += "admin-booked-day";
    } else if (isBlocked || isNonAllowedWeekday) {
      className += "admin-unavailable-day";
    } else {
      className += "admin-normal-day";
    }

    if (isSelected) {
      className += " admin-selected-day";
    }

    return <div className={className}>{day}</div>;
  };

  const viewTexts = {
    title: "Calendrier d'administration",
    instructions:
      "Cliquez sur une date pour la gérer (disponibilité, réservations)",
  };

  const { title, instructions } = viewTexts;

  return (
    <Paper elevation={3} className="calendar-paper">
      <Typography variant="h5" className="section-title">
        <EventAvailable className="section-icon" />
        {title}
      </Typography>

      <Typography variant="body2" className="calendar-instructions">
        {instructions}
      </Typography>

      <Box className="modern-calendar-container">
        <DatePicker
          selected={highlightedDate}
          onChange={handleDateSelection}
          inline
          locale={fr}
          monthsShown={1}
          renderDayContents={renderDayContents}
          disabledKeyboardNavigation
          calendarClassName={`custom-datepicker `}
          formatWeekDay={(nameOfDay) =>
            nameOfDay.charAt(0).toUpperCase() + nameOfDay.slice(1, 3)
          }
        />

        <CalendarLegend isAdmin />
      </Box>
    </Paper>
  );
};

export default AdminCalendar;
